import { useState, useEffect } from 'react';
import { D, st, apiPost, apiPut, apiGet } from '../constants';
import { Field } from './shared';

const PAYMENT_OPTIONS = [
  { key: 'check', icon: '💵', label: 'Check' },
  { key: 'cash', icon: '💴', label: 'Cash' },
  { key: 'venmo', icon: '💙', label: 'Venmo', handleKey: 'venmoHandle', placeholder: '@yourhandle' },
  { key: 'zelle', icon: '🟣', label: 'Zelle', handleKey: 'zelleContact', placeholder: 'Phone number or email' },
  { key: 'paypal', icon: '🔵', label: 'PayPal', handleKey: 'paypalUser', placeholder: 'PayPal username or email' },
  { key: 'cashapp', icon: '🟢', label: 'Cash App', handleKey: 'cashappTag', placeholder: '$cashtag' },
];

const fmt = n => '$' + (Number(n) || 0).toFixed(2);

export default function SignupWizard({ onComplete, onBack, onLogin }) {
  const [step, setStep] = useState(1);
  const [sessionId] = useState(() => crypto.randomUUID());
  useEffect(() => { apiPost('/analytics/event', { type: 'wizard_step', sessionId, step }).catch(() => {}); }, [step]);
  const [data, setData] = useState({
    name: '', ownerName: '', licenseNum: '', phone: '', email: '', address: '',
    ownerPin: '', ownerPinConfirm: '',
    paymentMethods: {},
    masterRate: 125, journeymanRate: 95, apprenticeRate: 65,
    matMarkup: 20, subMarkup: 10, overhead: 15, profit: 10, permitFee: 500,
    businessType: 'both', crewSize: 'small', serviceArea: '',
    notifyOptIn: true,
    referralCode: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeErr, setStripeErr] = useState('');

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const setPM = (k, v) => setData(d => ({ ...d, paymentMethods: { ...d.paymentMethods, [k]: v } }));

  const steps = [
    { num: 1, label: 'Company' },
    { num: 2, label: 'Account' },
    { num: 3, label: 'Payments' },
    { num: 4, label: 'Rates' },
    { num: 5, label: 'Profile' },
    { num: 6, label: 'Done' },
  ];

  const complete = async () => {
    if (!data.name?.trim()) { setError('Company name required.'); return; }
    if (!data.email) { setError('Email required.'); return; }
    if (!data.ownerPin || data.ownerPin.length < 4) { setError('PIN must be at least 4 digits.'); return; }
    setSubmitting(true); setError('');
    const markup = {
      Materials: data.matMarkup, Labor: 0, Subcontractor: data.subMarkup,
      masterRate: data.masterRate, journeymanRate: data.journeymanRate, apprenticeRate: data.apprenticeRate,
      overhead: data.overhead, profit: data.profit, permitFee: data.permitFee,
      businessType: data.businessType, crewSize: data.crewSize, serviceArea: data.serviceArea,
    };
    try {
      const result = await apiPost('/auth/signup', {
        companyName: data.name, email: data.email, ownerName: data.ownerName || 'Owner',
        pin: data.ownerPin, notifyOptIn: data.notifyOptIn,
        referredByCode: data.referralCode || undefined,
      });
      localStorage.setItem('eb:token', result.token);
      localStorage.setItem('eb:visited', '1');
      await apiPut('/data/settings', { markup, paymentMethods: data.paymentMethods }).catch(() => {});
      await apiPut('/data/company', { name: data.name, phone: data.phone || '', address: data.address || '', licenseNum: data.licenseNum || '' }).catch(() => {});
      setAccountCreated(true);
    } catch (e) {
      let msg = e.message || 'Signup failed.';
      try { msg = JSON.parse(msg).error || msg; } catch (_) {}
      setError(msg === 'Email already registered' ? 'That email is already registered. Use Log In instead.' : msg);
      setSubmitting(false);
    }
  };

  const connectStripe = async () => {
    setStripeBusy(true); setStripeErr('');
    try {
      const r = await apiGet('/connect/onboard');
      window.location.href = r.url;
    } catch (e) {
      setStripeErr(e.message || 'Failed to start Stripe setup');
      setStripeBusy(false);
    }
  };

  const inp = { ...st.input, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ minHeight: '100vh', background: D.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: 16, padding: '32px 28px', width: 'min(520px,95vw)', boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: D.muted, fontSize: 12, cursor: 'pointer', marginBottom: 8 }}>← Back to AmpBooks.com</button>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⚡</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: D.text }}>Welcome to AmpBooks</div>
          <div style={{ fontSize: 12, color: D.muted, marginTop: 4 }}>Let's get your company set up — takes about 2 minutes</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 28 }}>
          {steps.map(s => (
            <div key={s.num} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: step === s.num ? D.accent : step > s.num ? D.success : D.surface,
                border: `2px solid ${step === s.num ? D.accent : step > s.num ? D.success : D.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: step >= s.num ? '#fff' : D.muted,
              }}>
                {step > s.num ? '✓' : s.num}
              </div>
              <div style={{ fontSize: 9, color: step === s.num ? D.accent : D.muted, whiteSpace: 'nowrap' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {error && <div style={{ background: D.danger + '15', border: `1px solid ${D.danger}44`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: D.danger }}>{error}</div>}

        {step === 1 && <>
          <div style={{ ...st.h2, marginBottom: 16 }}>Company Information</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field label="Company Name"><input style={inp} value={data.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Smith Electric LLC" autoFocus /></Field>
            <Field label="Owner Name"><input style={inp} value={data.ownerName} onChange={e => set('ownerName', e.target.value)} placeholder="Your full name" /></Field>
            <Field label="Contractor License #"><input style={inp} value={data.licenseNum} onChange={e => set('licenseNum', e.target.value)} placeholder="e.g. EC1300456" /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Phone"><input style={inp} value={data.phone} onChange={e => set('phone', e.target.value)} placeholder="(239) 555-0100" /></Field>
              <Field label="Email"><input style={inp} type="email" value={data.email} onChange={e => set('email', e.target.value)} placeholder="you@company.com" /></Field>
            </div>
            <Field label="City / State"><input style={inp} value={data.address} onChange={e => set('address', e.target.value)} placeholder="Cape Coral, FL" /></Field>
            <Field label="Referral Code (optional)"><input style={inp} value={data.referralCode} onChange={e => set('referralCode', e.target.value.toUpperCase())} placeholder="e.g. ADAM-66CA" /></Field>
          </div>
          <button style={{ ...st.btn('primary'), marginLeft: 0, width: '100%', padding: '12px 0', fontSize: 14, marginTop: 20 }} onClick={() => { if (!data.name?.trim()) { setError('Please enter your company name.'); return; } setError(''); setStep(2); }}>
            Next →
          </button>
        </>}

        {step === 2 && <>
          <div style={{ ...st.h2, marginBottom: 8 }}>Owner Account</div>
          <div style={{ fontSize: 13, color: D.muted, marginBottom: 16, lineHeight: 1.6 }}>Create your owner PIN. This is required to sign in as Owner and access all features.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field label="Owner PIN (4–8 digits)">
              <input style={{ ...inp, fontSize: 20, textAlign: 'center', letterSpacing: 8 }} type="password" maxLength={8} value={data.ownerPin} onChange={e => set('ownerPin', e.target.value)} placeholder="" autoComplete="new-password" autoFocus />
            </Field>
            <Field label="Confirm PIN">
              <input style={{ ...inp, fontSize: 20, textAlign: 'center', letterSpacing: 8 }} type="password" maxLength={8} value={data.ownerPinConfirm} autoComplete="new-password" onChange={e => set('ownerPinConfirm', e.target.value)} placeholder="••••" />
            </Field>
            <div style={{ fontSize: 12, color: D.muted, padding: '8px 12px', background: D.surface, borderRadius: 7 }}>
              💡 Your crew will sign in by selecting their name. Only you need the owner PIN.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <button style={{ ...st.btn(), marginLeft: 0, flex: 1 }} onClick={() => setStep(1)}>← Back</button>
            <button style={{ ...st.btn('primary'), marginLeft: 0, flex: 2, padding: '12px 0', fontSize: 14 }} onClick={() => {
              if (!data.ownerPin || data.ownerPin.length < 4) { setError('PIN must be at least 4 digits.'); return; }
              if (data.ownerPin !== data.ownerPinConfirm) { setError("PINs don't match. Please try again."); return; }
              setError(''); setStep(3);
            }}>Next →</button>
          </div>
        </>}

        {step === 3 && <>
          <div style={{ ...st.h2, marginBottom: 8 }}>Payment Methods</div>
          <div style={{ fontSize: 13, color: D.muted, marginBottom: 16, lineHeight: 1.6 }}>How do you accept payment? This appears on invoices sent to clients. You can skip this and set it up later in Settings.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {PAYMENT_OPTIONS.map(opt => (
              <div key={opt.key} style={{ background: D.surface, borderRadius: 8, padding: '10px 14px', border: `1px solid ${data.paymentMethods[opt.key] ? D.accent : D.border}` }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: (data.paymentMethods[opt.key] && opt.handleKey) ? 8 : 0 }}>
                  <input type="checkbox" checked={!!data.paymentMethods[opt.key]} onChange={e => setPM(opt.key, e.target.checked)} style={{ width: 16, height: 16, accentColor: D.accent }} />
                  <span style={{ fontSize: 13, fontWeight: 500, color: D.text }}>{opt.icon} {opt.label}</span>
                </label>
                {opt.handleKey && data.paymentMethods[opt.key] && (
                  <input style={{ ...inp, fontSize: 12, marginTop: 6 }} value={data.paymentMethods[opt.handleKey] || ''} onChange={e => setPM(opt.handleKey, e.target.value)} placeholder={opt.placeholder} autoFocus />
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <button style={{ ...st.btn(), marginLeft: 0, flex: 1 }} onClick={() => setStep(2)}>← Back</button>
            <button style={{ ...st.btn('primary'), marginLeft: 0, flex: 2, padding: '12px 0', fontSize: 14 }} onClick={() => setStep(4)}>
              {Object.keys(data.paymentMethods).length === 0 ? 'Skip for now →' : 'Next →'}
            </button>
          </div>
        </>}

        {step === 4 && <>
          <div style={{ ...st.h2, marginBottom: 8 }}>Rates & Markup</div>
          <div style={{ fontSize: 13, color: D.muted, marginBottom: 16, lineHeight: 1.6 }}>These are used by the AI bid engine to generate accurate estimates. You can change them anytime in Settings.</div>

          <div style={{ fontSize: 12, fontWeight: 700, color: D.text, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>💼 Labor Rates (per hour)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
            <Field label="Master"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ color: D.muted, fontSize: 13 }}>$</span><input style={{ ...inp, flex: 1 }} type="number" min="0" value={data.masterRate} onChange={e => set('masterRate', parseFloat(e.target.value) || 0)} /></div></Field>
            <Field label="Journeyman"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ color: D.muted, fontSize: 13 }}>$</span><input style={{ ...inp, flex: 1 }} type="number" min="0" value={data.journeymanRate} onChange={e => set('journeymanRate', parseFloat(e.target.value) || 0)} /></div></Field>
            <Field label="Apprentice"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ color: D.muted, fontSize: 13 }}>$</span><input style={{ ...inp, flex: 1 }} type="number" min="0" value={data.apprenticeRate} onChange={e => set('apprenticeRate', parseFloat(e.target.value) || 0)} /></div></Field>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: D.text, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>📦 Markup</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            <Field label="Materials Markup"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input style={{ ...inp, flex: 1 }} type="number" min="0" max="100" value={data.matMarkup} onChange={e => set('matMarkup', parseFloat(e.target.value) || 0)} /><span style={{ color: D.muted, fontSize: 13 }}>%</span></div></Field>
            <Field label="Subcontractor Markup"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input style={{ ...inp, flex: 1 }} type="number" min="0" max="100" value={data.subMarkup} onChange={e => set('subMarkup', parseFloat(e.target.value) || 0)} /><span style={{ color: D.muted, fontSize: 13 }}>%</span></div></Field>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: D.text, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>📊 Overhead & Profit</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
            <Field label="Overhead %"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input style={{ ...inp, flex: 1 }} type="number" min="0" max="100" value={data.overhead} onChange={e => set('overhead', parseFloat(e.target.value) || 0)} /><span style={{ color: D.muted, fontSize: 13 }}>%</span></div></Field>
            <Field label="Profit %"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><input style={{ ...inp, flex: 1 }} type="number" min="0" max="100" value={data.profit} onChange={e => set('profit', parseFloat(e.target.value) || 0)} /><span style={{ color: D.muted, fontSize: 13 }}>%</span></div></Field>
            <Field label="Permit Fee"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ color: D.muted, fontSize: 13 }}>$</span><input style={{ ...inp, flex: 1 }} type="number" min="0" value={data.permitFee} onChange={e => set('permitFee', parseFloat(e.target.value) || 0)} /></div></Field>
          </div>

          <div style={{ background: D.surface, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 11, color: D.muted, lineHeight: 1.7 }}>
            💡 <strong style={{ color: D.text }}>Example:</strong> A 4-hour master job with $200 materials bills as Labor {fmt(data.masterRate * 4)} + Materials {fmt(200 * (1 + data.matMarkup / 100))} = <strong style={{ color: D.success }}>{fmt(data.masterRate * 4 + 200 * (1 + data.matMarkup / 100))}</strong> before tax
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...st.btn(), marginLeft: 0, flex: 1 }} onClick={() => setStep(3)}>← Back</button>
            <button style={{ ...st.btn('primary'), marginLeft: 0, flex: 2, padding: '12px 0', fontSize: 14 }} onClick={() => setStep(5)}>Next →</button>
          </div>
        </>}

        {step === 5 && <>
          <div style={{ ...st.h2, marginBottom: 8 }}>Business Profile</div>
          <div style={{ fontSize: 13, color: D.muted, marginBottom: 16, lineHeight: 1.6 }}>Helps tailor the AI bid engine and permit directory to your business.</div>

          <Field label="What type of work do you do?">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[['residential', '🏠 Residential only'], ['commercial', '🏢 Commercial only'], ['both', '⚡ Both residential & commercial']].map(([val, label]) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: D.surface, borderRadius: 8, border: `1px solid ${data.businessType === val ? D.accent : D.border}`, cursor: 'pointer' }}>
                  <input type="radio" name="bizType" value={val} checked={data.businessType === val} onChange={e => set('businessType', e.target.value)} style={{ accentColor: D.accent }} />
                  <span style={{ fontSize: 13, fontWeight: 500, color: D.text }}>{label}</span>
                </label>
              ))}
            </div>
          </Field>

          <Field label="How big is your crew?">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              {[['solo', 'Just me'], ['small', '2–5 people'], ['medium', '6–10 people'], ['large', '10+ people']].map(([val, label]) => (
                <button key={val} type="button" onClick={() => set('crewSize', val)} style={{ padding: '8px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', border: `1px solid ${data.crewSize === val ? D.accent : D.border}`, background: data.crewSize === val ? D.accent + '22' : 'transparent', color: data.crewSize === val ? D.accent : D.muted, fontWeight: data.crewSize === val ? 600 : 400 }}>
                  {label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Primary service area (city or ZIP code)">
            <input style={inp} value={data.serviceArea} onChange={e => set('serviceArea', e.target.value)} placeholder="e.g. Cape Coral, FL or 33904" />
          </Field>

          {data.crewSize === 'solo' && (
            <div style={{ background: D.accent + '11', border: `1px solid ${D.accent}33`, borderRadius: 8, padding: '10px 14px', marginTop: 8, fontSize: 12, color: D.muted, lineHeight: 1.6 }}>
              💡 Solo operator mode — payroll and time clock features will be simplified for single-person operation.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <button style={{ ...st.btn(), marginLeft: 0, flex: 1 }} onClick={() => setStep(4)}>← Back</button>
            <button style={{ ...st.btn('primary'), marginLeft: 0, flex: 2, padding: '12px 0', fontSize: 14 }} onClick={() => setStep(6)}>Next →</button>
          </div>
        </>}

        {step === 6 && <>
          <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: D.text, marginBottom: 8 }}>{data.name || 'Your company'} is ready!</div>
            <div style={{ fontSize: 13, color: D.muted, lineHeight: 1.7, marginBottom: 16, textAlign: 'left' }}>
              Here's what's been set up:<br />
              ✓ Company profile &amp; owner PIN<br />
              {Object.keys(data.paymentMethods).length > 0 && <>✓ Payment methods on invoices<br /></>}
              ✓ Labor rates &amp; markup<br />
              ✓ Business profile<br />
              <br />
              You can edit any of this later in <strong style={{ color: D.text }}>Settings</strong>. Your free trial runs for 7 days — no card required.
            </div>

            <div style={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 16, textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: D.accent, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>Your workflow</div>
              {[
                ['📋', 'Create a Bid', 'Describe the job — AI can write it for you'],
                ['✉', 'Send an Estimate', 'Client gets a professional email with a one-click approve button'],
                ['✅', 'Client Approves', 'They click the button — no account needed'],
                ['🧾', 'Invoice + Job auto-created', 'Ready to schedule and collect payment'],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: D.text }}>{title}</div>
                    <div style={{ fontSize: 11, color: D.muted }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', background: D.surface, borderRadius: 8, marginBottom: 20, cursor: 'pointer', textAlign: 'left' }}>
              <input type="checkbox" checked={data.notifyOptIn} onChange={e => set('notifyOptIn', e.target.checked)} style={{ width: 16, height: 16, accentColor: D.accent, marginTop: 1 }} />
              <span style={{ fontSize: 12, color: D.text }}>Email me about upcoming jobs and overdue client balances</span>
            </label>

            {!accountCreated ? <>
              <button style={{ ...st.btn('primary'), marginLeft: 0, width: '100%', padding: '13px 0', fontSize: 15 }} onClick={complete} disabled={submitting}>
                {submitting ? 'Setting up...' : '⚡ Enter AmpBooks'}
              </button>
              <button style={{ ...st.btn(), marginLeft: 0, width: '100%', padding: '10px 0', fontSize: 13, marginTop: 8 }} onClick={() => setStep(5)} disabled={submitting}>
                ← Edit Business Profile
              </button>
            </> : <>
              <div style={{ background: D.success + '15', border: `1px solid ${D.success}44`, borderRadius: 10, padding: '14px 16px', marginBottom: 16, textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: D.success, marginBottom: 8 }}>✓ Account created!</div>
                <div style={{ fontSize: 12, color: D.text, fontWeight: 600, marginBottom: 4 }}>💳 Accept card payments online</div>
                <div style={{ fontSize: 12, color: D.muted, lineHeight: 1.6, marginBottom: 12 }}>Connect a Stripe account so clients can pay invoices by card. Money goes directly to your bank — AmpBooks never holds your funds.</div>
                {stripeErr && <div style={{ color: D.danger, fontSize: 12, marginBottom: 8 }}>{stripeErr}</div>}
                <button style={{ ...st.btn('primary'), marginLeft: 0, width: '100%', background: '#635bff', borderColor: '#4f48cc' }} onClick={connectStripe} disabled={stripeBusy}>
                  {stripeBusy ? 'Connecting...' : '⚡ Connect with Stripe →'}
                </button>
              </div>
              <button style={{ ...st.btn('primary'), marginLeft: 0, width: '100%', padding: '13px 0', fontSize: 15 }} onClick={onComplete}>
                Enter AmpBooks →
              </button>
            </>}
          </div>
        </>}

      </div>
      {onLogin && (
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: D.muted }}>
          Already have an account?{' '}
          <span onClick={onLogin} style={{ color: D.accent, cursor: 'pointer', fontWeight: 600 }}>Log In →</span>
        </div>
      )}
    </div>
  );
}
